import React, { useEffect, useMemo, useRef, useState } from 'react';
import { currentConfig } from '../config/environment';
import locationService from '../services/locationService';
import { getCurrentUser, getEffectiveAccountId } from '../services/authorizationService';
import { useAuth } from '../AuthContext';

/** Parse Worker JSON + nested Google error string for quota signals (no secrets). */
function extractGbpQuotaMetadata(rawText) {
  try {
    const outer = JSON.parse(rawText);
    const innerStr = outer?.details;
    if (typeof innerStr === 'string') {
      const inner = JSON.parse(innerStr);
      const meta = inner?.error?.details?.find((d) => d?.['@type']?.includes?.('ErrorInfo'))?.metadata;
      return {
        quotaLimitValue: meta?.quota_limit_value ?? null,
        googleReason: meta?.reason ?? inner?.error?.message ?? null,
      };
    }
    if (outer?.error?.details && Array.isArray(outer.error.details)) {
      const meta = outer.error.details.find((d) => d?.['@type']?.includes?.('ErrorInfo'))?.metadata;
      return {
        quotaLimitValue: meta?.quota_limit_value ?? null,
        googleReason: meta?.reason ?? outer?.error?.message ?? null,
      };
    }
  } catch {
    /* fall through */
  }
  const qMatch = String(rawText).match(/"quota_limit_value"\s*:\s*"([^"]*)"/);
  return {
    quotaLimitValue: qMatch ? qMatch[1] : null,
    googleReason: null,
  };
}

/** Collapse legacy errors that concatenated HTTP status + full JSON body into one string. */
function sanitizeGbpUserMessage(raw, kind) {
  if (!raw || typeof raw !== 'string') return raw || '';
  const statusTail = raw.match(/\((\d{3})\)\s*(\{[\s\S]*)$/);
  if (statusTail) {
    const status = parseInt(statusTail[1], 10);
    const meta = extractGbpQuotaMetadata(statusTail[2]);
    if (status === 429 || status === 403) {
      return formatGbpHttpError(kind, status, meta);
    }
  }
  if (raw.length > 180 && raw.includes('"quota_limit_value"')) {
    const meta = extractGbpQuotaMetadata(raw);
    const m = raw.match(/\((\d{3})\)/);
    const status = m ? parseInt(m[1], 10) : 429;
    return formatGbpHttpError(kind, status, meta);
  }
  return raw;
}

/** User-facing GBP API error (avoids dumping raw JSON). */
function formatGbpHttpError(kind, status, quotaMeta) {
  const q = quotaMeta?.quotaLimitValue != null ? String(quotaMeta.quotaLimitValue) : '';
  if (status === 429 && q === '0') {
    return 'Google Business Profile: API access is not active for this Cloud project yet (quota is 0). In Google Cloud Console, enable My Business Account Management API and complete Google’s Business Profile API access approval so quota is granted. Then try again.';
  }
  if (status === 429) {
    return 'Google Business Profile: rate limit exceeded. Wait a few minutes and try again.';
  }
  if (status === 403) {
    return 'Google Business Profile: permission denied. Disconnect Google under Account settings and reconnect so Business Profile access is granted.';
  }
  return `Failed to ${kind} (HTTP ${status}). Check Google connection or try again later.`;
}

function formatGbpLocationLabel(location) {
  if (!location) return 'Unknown location';
  const title = location.locationName || location.name || location.title || 'Location';
  const address =
    location.address?.addressLines?.join(', ') ||
    location.address?.locality ||
    location.storefrontAddress?.addressLines?.join(', ') ||
    location.storefrontAddress?.locality ||
    '';
  return address ? `${title} — ${address}` : title;
}

export default function GoogleBusinessProfileLocations() {
  const { currentUser } = useAuth();
  const effectiveAccountId = getEffectiveAccountId();
  const adminUid = getCurrentUser()?.uid || currentUser?.uid || null;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [portalLocations, setPortalLocations] = useState([]);
  const [gbpAccounts, setGbpAccounts] = useState([]);
  const [selectedGbpAccountName, setSelectedGbpAccountName] = useState('');
  const [gbpLocations, setGbpLocations] = useState([]);

  const [savingByLocationId, setSavingByLocationId] = useState({});
  const loadEffectRunRef = useRef(0);

  const gbpLocationsByName = useMemo(() => {
    const map = new Map();
    (gbpLocations || []).forEach((l) => {
      if (l?.name) map.set(l.name, l);
    });
    return map;
  }, [gbpLocations]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    const load = async () => {
      loadEffectRunRef.current += 1;
      const effectRun = loadEffectRunRef.current;
      setIsLoading(true);
      setError('');

      try {
        if (!effectiveAccountId) {
          setPortalLocations([]);
          setGbpAccounts([]);
          setGbpLocations([]);
          setSelectedGbpAccountName('');
          return;
        }

        const locations = await locationService.getLocations(effectiveAccountId);
        if (cancelled) return;
        setPortalLocations(locations);

        if (!adminUid) {
          setGbpAccounts([]);
          setGbpLocations([]);
          setSelectedGbpAccountName('');
          return;
        }

        const accountsRes = await fetch(
          `${currentConfig.apiBaseUrl}/api/gbp/accounts?userId=${encodeURIComponent(adminUid)}&accountId=${encodeURIComponent(effectiveAccountId)}`,
          { signal: ac.signal }
        );
        const accountsBodyText = await accountsRes.text().catch(() => '');
        const quotaMeta = extractGbpQuotaMetadata(accountsBodyText);
        let apiHost = '';
        try {
          apiHost = new URL(currentConfig.apiBaseUrl).host;
        } catch {
          apiHost = '';
        }
        // #region agent log
        fetch('http://127.0.0.1:7927/ingest/42d17bd7-49fb-4648-aa8b-2494effe9e11', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '042f68' },
          body: JSON.stringify({
            sessionId: '042f68',
            location: 'GoogleBusinessProfileLocations.js:accountsFetch',
            message: 'gbp accounts fetch result',
            data: {
              hypothesisId: 'H1,H2,H3,H4',
              effectRun,
              httpStatus: accountsRes.status,
              ok: accountsRes.ok,
              apiHost,
              quotaLimitValue: quotaMeta.quotaLimitValue,
              googleReason: quotaMeta.googleReason ? String(quotaMeta.googleReason).slice(0, 160) : null,
            },
            timestamp: Date.now(),
            runId: 'post-fix',
          }),
        }).catch(() => {});
        // #endregion
        if (!accountsRes.ok) {
          throw new Error(formatGbpHttpError('load Google Business accounts', accountsRes.status, quotaMeta));
        }
        const accountsJson = JSON.parse(accountsBodyText);
        const accounts = accountsJson.accounts || [];
        setGbpAccounts(accounts);

        // Default selection: if any location already linked, use that account; else first account
        const linkedAccountName =
          locations.find((l) => l.gbpAccountName)?.gbpAccountName || accounts[0]?.name || '';
        setSelectedGbpAccountName(linkedAccountName);
      } catch (e) {
        if (cancelled || e?.name === 'AbortError') return;
        const msg = e?.message || 'Failed to load Google Business Profile data';
        const display = sanitizeGbpUserMessage(msg, 'load Google Business accounts');
        // #region agent log
        fetch('http://127.0.0.1:7927/ingest/42d17bd7-49fb-4648-aa8b-2494effe9e11', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '042f68' },
          body: JSON.stringify({
            sessionId: '042f68',
            location: 'GoogleBusinessProfileLocations.js:accountsCatch',
            message: 'gbp accounts error display',
            data: {
              hypothesisId: 'H5',
              msgLen: msg.length,
              displayLen: display.length,
              displayIsShort: display.length < 400,
            },
            timestamp: Date.now(),
            runId: 'post-fix',
          }),
        }).catch(() => {});
        // #endregion
        setError(display);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [adminUid, effectiveAccountId]);

  useEffect(() => {
    const loadLocations = async () => {
      setError('');
      setGbpLocations([]);

      if (!selectedGbpAccountName || !adminUid || !effectiveAccountId) return;

      try {
        const res = await fetch(
          `${currentConfig.apiBaseUrl}/api/gbp/locations?gbpAccountName=${encodeURIComponent(selectedGbpAccountName)}&userId=${encodeURIComponent(adminUid)}&accountId=${encodeURIComponent(effectiveAccountId)}`
        );
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          const quotaMeta = extractGbpQuotaMetadata(text);
          throw new Error(formatGbpHttpError('load Google Business locations', res.status, quotaMeta));
        }
        const json = await res.json();
        setGbpLocations(json.locations || []);
      } catch (e) {
        const msg = e?.message || 'Failed to load GBP locations';
        setError(sanitizeGbpUserMessage(msg, 'load Google Business locations'));
      }
    };

    loadLocations();
  }, [selectedGbpAccountName, adminUid, effectiveAccountId]);

  const saveLink = async (portalLocationId, gbpLocationName) => {
    if (!effectiveAccountId) return;
    setSavingByLocationId((prev) => ({ ...prev, [portalLocationId]: true }));
    setError('');

    try {
      const portalLoc = portalLocations.find((l) => l.id === portalLocationId);
      if (!portalLoc) throw new Error('Portal location not found');

      await locationService.updateLocation(
        portalLocationId,
        {
          ...portalLoc,
          gbpAccountName: selectedGbpAccountName || null,
          gbpLocationName: gbpLocationName || null,
          updatedAt: new Date().toISOString(),
        },
        effectiveAccountId
      );

      setPortalLocations((prev) =>
        prev.map((l) =>
          l.id === portalLocationId
            ? { ...l, gbpAccountName: selectedGbpAccountName || null, gbpLocationName: gbpLocationName || null }
            : l
        )
      );
    } catch (e) {
      setError(e?.message || 'Failed to save location mapping');
    } finally {
      setSavingByLocationId((prev) => ({ ...prev, [portalLocationId]: false }));
    }
  };

  if (!effectiveAccountId) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Google Business Profile Locations</h2>
        <p className="text-gray-600 text-sm">Select an account to configure location mappings.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Google Business Profile Locations</h2>
        <p className="text-gray-600 text-sm">
          Link each Shift location to a Google Business Profile location so we can pull reviews per location.
        </p>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{error}</div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading Google Business Profile…</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Google Business Profile account</label>
              <select
                value={selectedGbpAccountName}
                onChange={(e) => setSelectedGbpAccountName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select an account…</option>
                {gbpAccounts.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.accountName || a.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                If you don’t see an account, connect Google OAuth in this tab first.
              </p>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="text-sm font-medium text-gray-900 mb-3">Location mappings</div>

            {portalLocations.length === 0 ? (
              <div className="text-sm text-gray-600">No locations found for this account.</div>
            ) : (
              <div className="space-y-3">
                {portalLocations.map((pl) => {
                  const linked = pl.gbpLocationName ? gbpLocationsByName.get(pl.gbpLocationName) : null;
                  const isSaving = !!savingByLocationId[pl.id];

                  return (
                    <div key={pl.id} className="rounded-lg border p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">{pl.name || 'Location'}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {pl.address || [pl.city, pl.state].filter(Boolean).join(', ') || '—'}
                          </div>
                        </div>

                        <div className="flex-1 md:max-w-xl flex items-center gap-3">
                          <select
                            value={pl.gbpLocationName || ''}
                            onChange={(e) => saveLink(pl.id, e.target.value)}
                            disabled={!selectedGbpAccountName || isSaving}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                          >
                            <option value="">Not linked</option>
                            {gbpLocations.map((gl) => (
                              <option key={gl.name} value={gl.name}>
                                {formatGbpLocationLabel(gl)}
                              </option>
                            ))}
                          </select>

                          <div className="text-xs text-gray-500 w-24 text-right">
                            {isSaving ? 'Saving…' : linked ? 'Linked' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

